import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { encodeSvg } from '../../icons/Icons';
import { ButtonType } from './ButtonWidget';
import { ButtonWidget } from './ButtonWidgetShim';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { EventUtilsOptOut } from '../../utils/ReactHelpers';

// tslint:disable:max-line-length
const CloseButtonSvg = (fillColor: string = '#666') => `<svg xmlns="http://www.w3.org/2000/svg" class="$state$" width="14" height="14">
<path fill="${fillColor}" d="M7.7 7l4.7-4.7c.2-.2.2-.5 0-.7s-.5-.2-.7 0L7 6.3 2.3 1.6c-.2-.2-.5-.2-.7 0s-.2.5 0 .7L6.3 7l-4.7 4.7c-.2.2-.2.5 0 .7s.5.2.7 0L7 7.7l4.7 4.7c.2.2.5.2.7 0s.2-.5 0-.7L7.7 7z"/>
</svg>`;
// tslint:enable:max-line-length

const CloseButtonStyleName = TypeStyle.style({
  $nest: {
    '&&': { minHeight: 18, minWidth: 18, height: 18, width: 18 },
    '&::before': {
      content: `url(${encodeSvg(CloseButtonSvg('#666'))})`,
      display: 'inline-block',
      height: 14,
      width: 14,
    },
    '&:active::before, &:hover::before': {
      content: `url(${encodeSvg(CloseButtonSvg('#333'))})`,
    },
    '&.prominent::before': {
      content: `url(${encodeSvg(CloseButtonSvg('#333'))})`,
    },
    '&.prominent:active::before, &.prominent:hover::before': {
      content: `url(${encodeSvg(CloseButtonSvg('#000'))})`,
    },
    '&.dark::before': {
      content: `url(${encodeSvg(CloseButtonSvg('#fff'))}) !important`,
    }
  }
});

export interface CloseButtonProps extends Omit<ButtonWidget['props'], 'buttonType'> {
  useDarkStyling?: boolean;
}

export const CloseButtonWidget = React.forwardRef<ButtonWidget, CloseButtonProps>((props, ref) => {
  const { useDarkStyling, ...buttonProps } = props;
  return <ButtonWidget
    {...buttonProps}
    buttonType={ButtonType.IconOutline}
    className={TypeStyle.classes(props.className, CloseButtonStyleName, useDarkStyling && 'dark')}
    ref={ref}
    title={props.title || Messages.close()}
  />;
});
CloseButtonWidget.displayName = 'CloseButtonWidget';

const TapTargetStyleName = TypeStyle.style({
  alignItems: 'center',
  backgroundClip: 'content-box',
  boxSizing: 'border-box',
  cursor: 'auto',
  display: 'flex',
  flexShrink: 0,
  height: 30,
  justifyContent: 'center',
  outline: 'none',
  padding: 6,
  width: 30,
  $nest: {
    '&:hover': { backgroundColor: TabStyles.Colors.F3 },
    '&:hover>button::before': {
      content: `url(${encodeSvg(CloseButtonSvg('#333'))})`,
    },
    // hacks to hide the focus indicator for mouse
    '& > button:active': { boxShadow: 'none !important' },
    '& > button:active::after': { display: 'none !important' },
  }
});

export const DialogCloseButtonWidget = React.forwardRef<HTMLDivElement, Omit<ButtonWidget['props'], 'buttonType'>>((props, ref) =>
  <div {...EventUtilsOptOut} className={TapTargetStyleName} onClick={props.onClick as React.MouseEventHandler<HTMLElement>} ref={ref}>
    <CloseButtonWidget
      {...props}
      data-tb-test-id={makeTestId(props.testId!, 'CloseButton')}
      onClick={e => { e.stopPropagation(); props.onClick?.(e); }}
      testId={null!} // overridden by data-tb-test-id above
    />
  </div>
);
DialogCloseButtonWidget.displayName = 'DialogCloseButtonWidget';
